import { useMemo } from 'react';

const useQueryParams = <Key extends string = string, Types extends { [key in Key]: 'string' | 'number' | undefined } = { [key in Key]: 'string' | 'number' | undefined }>(
  keys: Key[],
  options?: { types?: Types },
): { [key in Key]?: Types[key] extends 'number' ? number : string } => {
  const search = window.location.search;
  // Serialize keys and types for stable useMemo deps
  const keysKey = JSON.stringify(keys);
  const typesKey = JSON.stringify(options?.types);

  const result = useMemo(() => {
    const parsedKeys: Key[] = JSON.parse(keysKey);
    const parsedTypes: Types | undefined = typesKey ? JSON.parse(typesKey) : undefined;
    const params = new URLSearchParams(search);
    const res: Record<string, string | number | undefined> = {};
    parsedKeys.forEach((key) => {
      const value = params.get(key);
      if (value === undefined || value === null) {
        res[key] = undefined;
        return;
      }
      const type = parsedTypes && parsedTypes[key];
      res[key] = type === 'number' ? +value : value;
    });
    return res as { [key in Key]?: Types[key] extends 'number' ? number : string };
  }, [search, keysKey, typesKey]);

  return result;
};

export default useQueryParams;
